import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { EnvironmentLoaderService } from './environment-loader.service';
import {
  ConnectorCountBySerialNumber,
  PaymentTerminalConnectorRequest,
  PayterTerminalConnector,
  PtsPaymentTerminal,
  PtsPaymentTerminalSearchEvent,
  ScreenUpdateCommand,
  TerminalSessionResponse,
  TerminalSessionSearchEvent,
  UiLogResponse,
  UiLogSearchEvent,
  ScreenUpdateRequest,
  ErrorMessageType,
  CustomStartCardRead,
  PayterTerminalLocation,
  ChargingStationLocationInfo,
  TerminalStatus,
  TerminalStatusLogSearchEvent,
  TerminalStatusLog,
  PayterTerminal,
} from 'src/app/features/payment-terminal/pts/pts-payment-terminal-model';
import { PagedResult } from '../models/paging';

@Injectable({
  providedIn: 'root',
})
export class PtsPaymentTerminalService {
  private http = inject(HttpClient);
  private environmentLoaderService = inject(EnvironmentLoaderService);

  apiProxyUrl: string;
  apiPayterUrl: string;

  constructor() {
    this.apiProxyUrl = this.environmentLoaderService.config['serviceBaseUrl'] + '/api/v1/admin/PtsProxy';
    this.apiPayterUrl = this.environmentLoaderService.config['serviceBaseUrl'] + '/api/v1/admin/PtsPayter';
  }

  patchPaymentTerminalStatus(paymentTerminalIdentifier: string, statuses: TerminalStatus[], note: string): Observable<void> {
    const url = `${this.apiProxyUrl}/terminal/${paymentTerminalIdentifier}/statuses`;
    return this.http.patch<void>(url, { statuses, note });
  }

  getPaymentTerminals(searchEvent?: PtsPaymentTerminalSearchEvent): Observable<PagedResult<PtsPaymentTerminal>> {
    const url = `${this.apiProxyUrl}/terminal`;
    let params = new HttpParams();
    const paging = searchEvent?.paging;
    const sorting = searchEvent?.sorting;

    if (searchEvent?.query) {
      params = params.append('q', searchEvent.query);
    }
    if (searchEvent?.chargingStationLocationIdentifier) {
      params = params.append('chargingStationLocationIdentifier', searchEvent.chargingStationLocationIdentifier);
    }
    if (searchEvent?.terminalConnectionStatus) {
      params = params.append('terminalConnectionStatus', searchEvent.terminalConnectionStatus);
    }
    if (searchEvent?.terminalStatuses && searchEvent.terminalStatuses.length > 0) {
      for (const status of searchEvent.terminalStatuses) {
        params = params.append('terminalStatuses', status);
      }
    }
    if (paging?.pageIndex) {
      params = params.append('page', paging.pageIndex);
    }
    if (paging?.pageSize) {
      params = params.append('pageSize', paging.pageSize);
    }
    if (sorting?.active) {
      params = params.append('sortby', sorting.active);
    }
    if (sorting?.direction) {
      params = params.append('sortdirection', sorting.direction);
    }

    return this.http.get<PagedResult<PtsPaymentTerminal>>(url, {
      params,
    });
  }
  getSerialNumbersWithoutAttachedConnectors(): Observable<string[]> {
    const url = `${this.apiPayterUrl}/serialNumbersWithoutAttachedConnectors`;
    return this.http.get<string[]>(url);
  }
  getPaymentTerminal(paymentTerminalIdentifier: string): Observable<PtsPaymentTerminal> {
    const url = `${this.apiProxyUrl}/terminal/${paymentTerminalIdentifier}`;
    return this.http.get<PtsPaymentTerminal>(url);
  }

  deletePaymentTerminalConnectors(serialNumber: string) {
    const url = `${this.apiPayterUrl}/${serialNumber}/Connectors`;
    return this.http.delete(url);
  }
  savePaymentTerminalConnectorRelation(
    serialNumber: string,
    paymentTerminalConnectorRequest: PaymentTerminalConnectorRequest
  ): Observable<void> {
    const url = `${this.apiPayterUrl}/${serialNumber}/Connectors`;
    return this.http.post<void>(url, paymentTerminalConnectorRequest);
  }

  getPayterTerminalConnectors(serialNumber: string): Observable<PayterTerminalConnector[]> {
    const url = `${this.apiPayterUrl}/${serialNumber}/Connectors`;
    return this.http.get<PayterTerminalConnector[]>(url);
  }

  getConnectorsByTerminalSerialNumbers(serialNumbers: string[]): Observable<ConnectorCountBySerialNumber[]> {
    const url = `${this.apiPayterUrl}/ConnectorsCount`;
    let params = new HttpParams();
    params = params.append('serialNumbers', serialNumbers.join('|'));
    return this.http.get<ConnectorCountBySerialNumber[]>(url, {
      params,
    });
  }

  getChargingStationInfoForTerminals(serialNumbers: string[]): Observable<ChargingStationLocationInfo[]> {
    const url = `${this.apiPayterUrl}/ChargingStationLocationInfo`;
    let params = new HttpParams();
    params = params.append('serialNumbers', serialNumbers.join('|'));
    return this.http.get<ChargingStationLocationInfo[]>(url, {
      params,
    });
  }

  getPayterTerminalLocations(request?: GetPayterTerminalLocationsRequest): Observable<PayterTerminalLocation[]> {
    const url = `${this.apiPayterUrl}/locations`;

    return this.http.get<PayterTerminalLocation[]>(url, {
      params: request?.query ? new HttpParams().append('query', request.query) : undefined,
    });
  }

  getUiLogs(uiLogSearchEvent: UiLogSearchEvent): Observable<PagedResult<UiLogResponse>> {
    const url = `${this.apiProxyUrl}/Terminal/${uiLogSearchEvent?.serialNumber}/uilogs`;
    let params = new HttpParams();

    if (uiLogSearchEvent.paging?.pageIndex) {
      params = params.append('page', uiLogSearchEvent.paging.pageIndex);
    }
    if (uiLogSearchEvent.paging?.pageSize) {
      params = params.append('pageSize', uiLogSearchEvent.paging.pageSize);
    }

    return this.http.get<PagedResult<UiLogResponse>>(url, {
      params,
    });
  }

  getTerminalSessions(terminalSessionSearchEvent: TerminalSessionSearchEvent): Observable<PagedResult<TerminalSessionResponse>> {
    const url = `${this.apiPayterUrl}/TerminalSession`;
    let params = new HttpParams();

    if (terminalSessionSearchEvent.query) {
      params = params.append('q', terminalSessionSearchEvent.query);
    }
    if (terminalSessionSearchEvent.serialNumber) {
      params = params.append('serialNumber', terminalSessionSearchEvent.serialNumber);
    }
    if (terminalSessionSearchEvent?.start && terminalSessionSearchEvent?.end) {
      params = params.append('start', terminalSessionSearchEvent.start);
      params = params.append('end', terminalSessionSearchEvent.end);
    }
    if (terminalSessionSearchEvent.paging?.pageIndex) {
      params = params.append('page', terminalSessionSearchEvent.paging.pageIndex);
    }
    if (terminalSessionSearchEvent.paging?.pageSize) {
      params = params.append('pageSize', terminalSessionSearchEvent.paging.pageSize);
    }
    if (terminalSessionSearchEvent?.sorting?.active) {
      params = params.append('sortby', terminalSessionSearchEvent.sorting.active);
    }
    if (terminalSessionSearchEvent?.sorting?.direction) {
      params = params.append('sortdirection', terminalSessionSearchEvent.sorting.direction);
    }

    return this.http.get<PagedResult<TerminalSessionResponse>>(url, {
      params,
    });
  }

  sendCommand(
    serialNumber: string,
    screenUpdateCommand: ScreenUpdateCommand,
    errorMessageType: ErrorMessageType = ErrorMessageType.TerminalIsOutOfOrder,
    customErrorMessage: string | null = null,
    amount: number | null = null,
    urlForQrCode: string | null = null
  ): Observable<void> {
    switch (screenUpdateCommand) {
      case ScreenUpdateCommand.Reset: {
        const screenUpdateUrl = `${this.apiProxyUrl}/screenupdate/reset`;
        const screenUpdateRequest = {
          serialNumber: serialNumber,
          screenId: 'start',
          response: 'start',
        } as ScreenUpdateRequest;
        return this.http.post<void>(screenUpdateUrl, screenUpdateRequest);
      }
      case ScreenUpdateCommand.StartCardRead: {
        const screenUpdateUrl = `${this.apiProxyUrl}/screenupdate/custom-start-card-read`;
        const customStartCardRead = {
          serialNumber: serialNumber,
          amount: amount,
        } as CustomStartCardRead;
        return this.http.post<void>(screenUpdateUrl, customStartCardRead);
      }
      case ScreenUpdateCommand.StopCardRead: {
        const screenUpdateUrl = `${this.apiProxyUrl}/screenupdate/stop-card-read/${serialNumber}`;
        return this.http.put<void>(screenUpdateUrl, null);
      }
      case ScreenUpdateCommand.Error: {
        const screenUpdateUrl = `${this.apiProxyUrl}/screenupdate/error/${errorMessageType}`;
        const screenUpdateRequest = {
          serialNumber: serialNumber,
          screenId: '',
          response: '',
          customErrorMessage: customErrorMessage,
        } as ScreenUpdateRequest;
        return this.http.post<void>(screenUpdateUrl, screenUpdateRequest);
      }
      case ScreenUpdateCommand.Connectors: {
        const screenUpdateUrl = `${this.apiProxyUrl}/screenupdate/start-ui-callback/`;
        const screenUpdateRequest = {
          serialNumber: serialNumber,
          screenId: 'start',
          response: 'start',
        } as ScreenUpdateRequest;
        return this.http.post<void>(screenUpdateUrl, screenUpdateRequest);
      }
      case ScreenUpdateCommand.SendQrCode: {
        const screenUpdateUrl = `${this.apiProxyUrl}/screenupdate/send-qr-code/`;
        const screenUpdateRequest = {
          serialNumber: serialNumber,
          urlForQrCode: urlForQrCode,
          screenId: '',
          response: '',
        } as ScreenUpdateRequest;
        return this.http.post<void>(screenUpdateUrl, screenUpdateRequest);
      }
    }
  }

  getPayterTerminalBySerialNumber(serialNumber: string): Observable<PayterTerminal> {
    const url = `${this.apiProxyUrl}/terminal/${serialNumber}`;
    return this.http.get<PayterTerminal>(url);
  }

  revertReservedAmount(terminalSession: TerminalSessionResponse): Observable<void> {
    const url = `${this.apiProxyUrl}/terminalSession/${terminalSession.chargingToken}/revertReservedAmount`;
    return this.http.put<void>(url, null);
  }

  getTerminalStatusLogs(
    terminalIdentifier: string,
    terminalStatusLogSearchEvent: TerminalStatusLogSearchEvent
  ): Observable<PagedResult<TerminalStatusLog>> {
    const url = `${this.apiProxyUrl}/terminal/${terminalIdentifier}/status-logs`;

    let params = new HttpParams();

    if (terminalStatusLogSearchEvent.paging?.pageIndex) {
      params = params.append('page', terminalStatusLogSearchEvent.paging.pageIndex);
    }
    if (terminalStatusLogSearchEvent.paging?.pageSize) {
      params = params.append('pageSize', terminalStatusLogSearchEvent.paging.pageSize);
    }
    if (terminalStatusLogSearchEvent?.sorting?.active) {
      params = params.append('sortby', terminalStatusLogSearchEvent.sorting.active);
    }
    if (terminalStatusLogSearchEvent?.sorting?.direction) {
      params = params.append('sortdirection', terminalStatusLogSearchEvent.sorting.direction);
    }

    return this.http.get<PagedResult<TerminalStatusLog>>(url, {
      params,
    });
  }
}

export interface GetPayterTerminalLocationsRequest {
  query?: string;
}
